import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardList, FileCheck2, Plus, ShieldCheck, Workflow } from 'lucide-react';

import { chamarApi } from '@/nucleo/auth/cliente-api';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/base/dialog';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';

const NS = '/bff/v1/j10-s01-admin-tipos-solicitacao';

/** Uma linha de `tipos_solicitacao_servico`, como o BFF a devolve. */
interface TipoDeSolicitacao {
  id: string;
  codigo: string;
  nome: string;
  descricao: string | null;
  tipo_recurso: string | null;
  exige_validacao_documental: boolean | null;
  exige_aprovacao_dteg: boolean | null;
  prazo_analise_dias: number | null;
  ativo: boolean | null;
  objeto_tipo: string | null;
  unidade_destino: string | null;
  fila: string | null;
  alcada: string | null;
}

interface Alerta {
  codigo: string;
  mensagem: string;
}

interface EstadoDaTela {
  titulo: string;
  descricao: string;
  alertas: Alerta[];
  tipos: TipoDeSolicitacao[];
}

const OBJETOS = [
  { valor: 'ORDEM_SERVICO', rotulo: 'Ordem de serviço' },
  { valor: 'CADASTRO_CREDENCIADO', rotulo: 'Cadastro do credenciado' },
] as const;

type ObjetoTipo = (typeof OBJETOS)[number]['valor'];

interface Formulario {
  codigo: string;
  nome: string;
  descricao: string;
  objetoTipo: ObjetoTipo;
  unidadeDestino: string;
  fila: string;
  alcada: string;
  prazoDias: string;
  exigeComprovante: boolean;
}

const FORMULARIO_VAZIO: Formulario = {
  codigo: '',
  nome: '',
  descricao: '',
  objetoTipo: 'ORDEM_SERVICO',
  unidadeDestino: '',
  fila: '',
  alcada: '',
  prazoDias: '5',
  exigeComprovante: true,
};

/**
 * Conferência feita no navegador, espelhando `CriarTipoSolicitacaoDto` da API.
 *
 * Existe para o erro aparecer no campo, e não como recusa genérica depois de
 * uma ida à rede. A API continua validando — esta é a primeira barreira, não a
 * única.
 */
function conferir(f: Formulario): Partial<Record<keyof Formulario, string>> {
  const erros: Partial<Record<keyof Formulario, string>> = {};
  const prazo = Number(f.prazoDias);

  if (f.codigo.trim().length < 3 || f.codigo.trim().length > 40) {
    erros.codigo = 'O código tem de 3 a 40 caracteres.';
  }
  if (f.nome.trim().length < 5 || f.nome.trim().length > 100) {
    erros.nome = 'O nome tem de 5 a 100 caracteres.';
  }
  if (f.descricao.trim().length < 10 || f.descricao.trim().length > 300) {
    erros.descricao = 'A descrição tem de 10 a 300 caracteres.';
  }
  if (f.unidadeDestino.trim().length < 2 || f.unidadeDestino.trim().length > 50) {
    erros.unidadeDestino = 'A unidade de destino tem de 2 a 50 caracteres.';
  }
  if (f.fila.trim().length < 3 || f.fila.trim().length > 80) {
    erros.fila = 'A fila tem de 3 a 80 caracteres.';
  }
  if (f.alcada.trim().length < 3 || f.alcada.trim().length > 80) {
    erros.alcada = 'A alçada tem de 3 a 80 caracteres.';
  }
  if (!Number.isInteger(prazo) || prazo < 1 || prazo > 90) {
    erros.prazoDias = 'O prazo é um número inteiro de 1 a 90 dias.';
  }
  return erros;
}

/**
 * `/admin/tipos-solicitacao` — o catálogo de pedidos que o credenciado abre no
 * portal.
 *
 * DIFERENTE DAS DEMAIS TELAS DESTE BLOCO, esta consulta a API de verdade
 * (`j10-s01-admin-tipos-solicitacao`), e o cadastro grava. Por isso ela depende
 * de a API estar no ar para apresentar dado, e um estado vazio aqui não é
 * defeito de interface.
 *
 * NÃO EXISTE INTERRUPTOR "ATIVO" NEM LÁPIS DE EDITAR. O protótipo traz os dois
 * por linha, mas o controlador só expõe `criar_tipo_vigente` — não há verbo que
 * alterne a situação nem que edite um tipo existente. Pela regra 7 das regras
 * de interface, o que o usuário não pode fazer não é renderizado; a situação
 * continua visível como selo de leitura, porque ela é informação, não ação. Os
 * dois voltam junto com os endpoints.
 */
export function TiposSolicitacaoPage() {
  const [estado, setEstado] = useState<EstadoDaTela | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroDeCarga, setErroDeCarga] = useState(false);

  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState<Formulario>(FORMULARIO_VAZIO);
  const [erros, setErros] = useState<Partial<Record<keyof Formulario, string>>>({});
  const [gravando, setGravando] = useState(false);
  const [erroDeGravacao, setErroDeGravacao] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroDeCarga(false);
    try {
      const r = await chamarApi<{ state: EstadoDaTela }>(`${NS}/state`);
      setEstado(r.state);
    } catch {
      setErroDeCarga(true);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const tipos = useMemo(() => estado?.tipos ?? [], [estado]);

  async function criar() {
    const encontrados = conferir(form);
    setErros(encontrados);
    if (Object.keys(encontrados).length > 0) {
      return;
    }

    setGravando(true);
    setErroDeGravacao(null);
    try {
      const r = await chamarApi<{ state: EstadoDaTela }>(`${NS}/verbs/criar_tipo_vigente`, {
        metodo: 'POST',
        corpo: {
          codigo: form.codigo.trim(),
          nome: form.nome.trim(),
          descricao: form.descricao.trim(),
          objetoTipo: form.objetoTipo,
          unidadeDestino: form.unidadeDestino.trim(),
          fila: form.fila.trim(),
          alcada: form.alcada.trim(),
          prazoDias: Number(form.prazoDias),
          exigeComprovante: form.exigeComprovante,
          // A chave nasce do código, que é único na tabela: reenviar o mesmo
          // formulário não cria um segundo tipo.
          chaveIdempotencia: `j10-tipo-${form.codigo.trim().toLowerCase()}`,
        },
      });
      setEstado(r.state);
      setAberto(false);
      setForm(FORMULARIO_VAZIO);
    } catch {
      setErroDeGravacao(
        'Não foi possível cadastrar o tipo. Confira os campos e tente de novo; se continuar, o código pode já estar em uso.',
      );
    } finally {
      setGravando(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={estado?.titulo ?? 'Tipos de solicitação de serviço'}
        description={
          estado?.descricao ??
          'Configure os tipos disponíveis no portal para solicitações dos credenciados.'
        }
      >
        <Button onClick={() => setAberto(true)} disabled={carregando || erroDeCarga}>
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          Novo tipo
        </Button>
      </PageHeader>

      {erroDeCarga ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar os tipos de solicitação agora. Verifique sua conexão e tente novamente."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void carregar() }}
        />
      ) : carregando ? (
        <Spinner />
      ) : (
        <>
          {estado?.alertas.map((a) => (
            <p key={a.codigo} role="alert" className="text-sm text-destructive-text">
              {a.mensagem}
            </p>
          ))}

          {tipos.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="Nenhum tipo de solicitação cadastrado"
              description="Enquanto não houver tipo em circulação, o credenciado não consegue abrir pedido pelo portal."
              actionLabel="Novo tipo"
              onAction={() => setAberto(true)}
            />
          ) : (
            <ul className="space-y-3">
              {tipos.map((t) => (
                <li key={t.id}>
                  <CartaoDoTipo tipo={t} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo tipo de solicitação</DialogTitle>
            <DialogDescription>
              O tipo passa a aparecer no portal do credenciado assim que for cadastrado.
            </DialogDescription>
          </DialogHeader>

          {erroDeGravacao && (
            <p role="alert" className="text-sm text-destructive-text">
              {erroDeGravacao}
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              id="codigo"
              rotulo="Código"
              ajuda="Identifica o tipo no resto do sistema. Não se repete."
              erro={erros.codigo}
            >
              <Input
                id="codigo"
                value={form.codigo}
                onChange={(ev) => setForm({ ...form, codigo: ev.target.value.toUpperCase() })}
                placeholder="TROCA_VEICULO"
              />
            </Campo>

            <Campo id="nome" rotulo="Nome" erro={erros.nome}>
              <Input
                id="nome"
                value={form.nome}
                onChange={(ev) => setForm({ ...form, nome: ev.target.value })}
                placeholder="Troca de veículo"
              />
            </Campo>

            <div className="sm:col-span-2">
              <Campo id="descricao" rotulo="Descrição" erro={erros.descricao}>
                <Textarea
                  id="descricao"
                  rows={3}
                  value={form.descricao}
                  onChange={(ev) => setForm({ ...form, descricao: ev.target.value })}
                  placeholder="Substituição do veículo vinculado à ordem de serviço, sujeita a validação documental."
                />
              </Campo>
            </div>

            <Campo id="objeto" rotulo="O que o pedido altera">
              <Select
                value={form.objetoTipo}
                onValueChange={(v) => setForm({ ...form, objetoTipo: v as ObjetoTipo })}
              >
                <SelectTrigger id="objeto">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OBJETOS.map((o) => (
                    <SelectItem key={o.valor} value={o.valor}>
                      {o.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo id="prazo" rotulo="Prazo de análise, em dias" erro={erros.prazoDias}>
              <Input
                id="prazo"
                type="number"
                min={1}
                max={90}
                value={form.prazoDias}
                onChange={(ev) => setForm({ ...form, prazoDias: ev.target.value })}
              />
            </Campo>

            <Campo id="unidade" rotulo="Unidade de destino" erro={erros.unidadeDestino}>
              <Input
                id="unidade"
                value={form.unidadeDestino}
                onChange={(ev) => setForm({ ...form, unidadeDestino: ev.target.value })}
                placeholder="DTP"
              />
            </Campo>

            <Campo id="fila" rotulo="Fila" erro={erros.fila}>
              <Input
                id="fila"
                value={form.fila}
                onChange={(ev) => setForm({ ...form, fila: ev.target.value })}
                placeholder="TROCAS_VEICULO"
              />
            </Campo>

            <Campo id="alcada" rotulo="Alçada responsável" erro={erros.alcada}>
              <Input
                id="alcada"
                value={form.alcada}
                onChange={(ev) => setForm({ ...form, alcada: ev.target.value })}
                placeholder="DTP_TECNICA"
              />
            </Campo>

            <div className="flex items-center gap-3 sm:col-span-2">
              <Switch
                id="comprovante"
                checked={form.exigeComprovante}
                onCheckedChange={(v) => setForm({ ...form, exigeComprovante: v })}
              />
              <Label htmlFor="comprovante" className="font-normal">
                Exige comprovante anexado pelo credenciado
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAberto(false)} disabled={gravando}>
              Cancelar
            </Button>
            <Button onClick={() => void criar()} disabled={gravando}>
              {gravando ? 'Cadastrando…' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Campo({
  id,
  rotulo,
  ajuda,
  erro,
  children,
}: {
  id: string;
  rotulo: string;
  ajuda?: string;
  erro?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{rotulo}</Label>
      {children}
      {erro ? (
        <p className="text-xs text-destructive-text">{erro}</p>
      ) : (
        ajuda && <p className="text-xs text-muted-foreground">{ajuda}</p>
      )}
    </div>
  );
}

function CartaoDoTipo({ tipo }: { tipo: TipoDeSolicitacao }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Workflow className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <h2 className="text-sm font-semibold">{tipo.nome}</h2>
            <Badge variant="outline" className="font-mono text-xs">
              {tipo.codigo}
            </Badge>
            {tipo.exige_aprovacao_dteg && (
              <Badge className="gap-1">
                <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                DTEG
              </Badge>
            )}
            {tipo.exige_validacao_documental && (
              <Badge variant="secondary" className="gap-1">
                <FileCheck2 className="h-3 w-3" aria-hidden="true" />
                Documentos
              </Badge>
            )}
            {tipo.tipo_recurso && <Badge variant="secondary">{tipo.tipo_recurso}</Badge>}
          </div>

          {tipo.descricao && <p className="text-sm text-muted-foreground">{tipo.descricao}</p>}

          <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
            {tipo.prazo_analise_dias != null && (
              <div className="flex gap-1">
                <dt>Prazo de análise:</dt>
                <dd className="font-medium text-foreground">
                  {tipo.prazo_analise_dias} {tipo.prazo_analise_dias === 1 ? 'dia' : 'dias'}
                </dd>
              </div>
            )}
            {tipo.unidade_destino && (
              <div className="flex gap-1">
                <dt>Destino:</dt>
                <dd className="font-medium text-foreground">{tipo.unidade_destino}</dd>
              </div>
            )}
            {tipo.fila && (
              <div className="flex gap-1">
                <dt>Fila:</dt>
                <dd className="font-medium text-foreground">{tipo.fila}</dd>
              </div>
            )}
            {tipo.alcada && (
              <div className="flex gap-1">
                <dt>Alçada:</dt>
                <dd className="font-medium text-foreground">{tipo.alcada}</dd>
              </div>
            )}
          </dl>
        </div>

        {/*
          Selo de LEITURA, não interruptor: não há verbo que alterne a situação
          de um tipo (ver o comentário da tela). A informação fica; a ação, não.
        */}
        {/*
          Sem `label`: o StatusBadge humaniza o próprio código do status, e é
          para isso que ele existe. Passar uma frase pronta faria o humanizador
          capitalizar palavra do meio ("Em Circulação"), que é maiúscula errada.
        */}
        <StatusBadge status={tipo.ativo ? 'ativo' : 'inativo'} className="shrink-0" />
      </CardContent>
    </Card>
  );
}

export default TiposSolicitacaoPage;
